import { ThemeProvider } from '@energir/design-system';
import tailwind from '@energir/design-system/css?url';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider as JotaiProvider } from 'jotai';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Outlet, Scripts, ScrollRestoration } from 'react-router';
import { store } from '~/atoms/store';
import { PageViewEvent } from '~/components/PageViewEvent';
import { WebVitals } from '~/components/WebVitals';
import { useNecTranslations } from '~/hooks/useNecTranslations';
import { queryClient } from '~/queries/queryClient';
import i18n from '~/setup/i18n';
import '~/setup/yupValidationMethods';
import { initOneTrust } from '~/utils/onetrust';
import { initRecaptcha } from '~/utils/captcha';
import { TagManagerProvider } from '~/utils/tagManager';
import stylesheet from './app.css?url';

export const links = () => [
    { rel: 'stylesheet', href: stylesheet },
    { rel: 'stylesheet', href: tailwind },
];

export function Layout({ children }: { children: ReactNode }) {
    return (
        <html lang="fr" translate="no">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta name="theme-color" content="#0047BB" />
                <link rel="icon" href="/favicon.ico" />
                <script src="/config.js"></script>
                {/* Google Tag Manager */}
                <script
                    dangerouslySetInnerHTML={{
                        __html: `
                            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                            })(window,document,'script','dataLayer','GTM-M23DTV3');
                            window.dataLayer = window.dataLayer || [];
                        `,
                    }}
                />
                <link rel="stylesheet" href={stylesheet} />
                <link rel="stylesheet" href={tailwind} />
            </head>
            <body>
                {/* Google Tag Manager (noscript) */}
                <noscript>
                    <iframe
                        src="https://www.googletagmanager.com/ns.html?id=GTM-M23DTV3"
                        height="0"
                        width="0"
                        style={{ display: 'none', visibility: 'hidden' }}
                    />
                </noscript>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    return (
        <I18nextProvider i18n={i18n}>
            <AppContent />
        </I18nextProvider>
    );
}

function AppContent() {
    const { t, language } = useNecTranslations();

    useEffect(() => {
        document.documentElement.lang = language;
        if (window.OneTrust?.changeLanguage) {
            window.OneTrust.changeLanguage(language);
        }
    }, [language]);

    useEffect(() => {
        void initOneTrust();
        void initRecaptcha();
    }, []);

    return (
        <JotaiProvider store={store}>
            <QueryClientProvider client={queryClient}>
                <TagManagerProvider>
                    <WebVitals />
                    <PageViewEvent />
                    <ThemeProvider language={language} formErrorMessageFunction={t}>
                        <Outlet />
                    </ThemeProvider>
                </TagManagerProvider>
            </QueryClientProvider>
        </JotaiProvider>
    );
}

export function ErrorBoundary() {
    const { t } = useNecTranslations();

    return (
        <div className="flex min-h-screen items-center justify-center p-8">
            <p className="text-red-500">{t('GENERIC_ASYNC_ERROR')}</p>
        </div>
    );
}
